import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MASKS_TYPE, applyFontsOnThePage, containerBackground, contentHeight, pageBackground } from '../../constants/constants';
import { effectiveZoom } from '../../utils/zoom';
import {
  contentPadding, masksDefaultHeight, navbarHeight, pageHeight, canvasLeftShift,
} from '../../constants/gridConfig';
import { useStore } from '../../store/store';
import './Middle.css';
import { pageWidth } from '../../constants/gridConfig';
import { ProposalList } from '../MultipleDocuments/ProposalList';
import InfinitePage, { getCanvasScroller, getVListScrollOffset, scrollVListTo } from './InfinitePage';
import Container from '../Container/Container';
import Loader from '../../Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useIsFormView } from '../../contexts/OverlayContext';
import { getVariablesGroupedByContainer } from '../../utils/variables';
import FormViewer from '../VariableHandler/FormViewer/FormViewer';
import { isPageBackgroundId } from '../../utils/pageBackground';
import PageBackgroundFrame, { NOTICE_SPACE as PAGE_BACKGROUND_NOTICE_SPACE } from '../common/PageBackgroundFrame';

const selector = (state) => ({
  moveBoxInfinite: state.moveBoxInfinite,
  boxesId: state.boxes.map((b) => b.id),
  configuration: state.configuration,
  handleUnSelectBox: state.handleUnSelectBox,
  sliderHeight: state.slider.from.value,
  fontSize: state.configuration.fontSize,
  fontFamily: state.configuration.fontFamily,
  fontFamilyList: state.configuration.fontFamilyList,
  maskEditing: state.maskEditing,
  maskList: state.maskList,
  proposalList: state.proposalList,
  masks: state.masks.filter((mask) => state.maskEditing.includes(mask.id)),
  landscape: state.landscape,
  currentDocument: state.currentDocument,
  handleUnSelectContainer: state.handleUnSelectContainer,
  currentProposal: state.currentProposal,
  zoomLevel: state.zoomLevel
});


const Middle = () => {
  const {
    fontSize,
    fontFamily,
    fontFamilyList,
    configuration,
    handleUnSelectBox,
    maskEditing,
    proposalList,
    masks,
    landscape,
    currentDocument,
    handleUnSelectContainer,
    currentProposal,
    zoomLevel
  } = useStore(selector);
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))
  const setCanScrollX = useStore(({ setCanScrollX }) => setCanScrollX);
  const canScrollX = useStore(({ canScrollX }) => canScrollX);
  const panModeActive = useStore(({ panModeActive }) => panModeActive);
  const setPanModeActive = useStore(({ setPanModeActive }) => setPanModeActive);

  // Aperçu du formulaire à la place du document (mode proposal uniquement,
  // cf. LeftSide.jsx qui affiche FormItemsArray dans le panel gauche pendant
  // ce mode) : mêmes ingrédients que getVariablesGroupedByContainer dans
  // LeftSide.jsx/VariableDrawer.jsx, recalculés ici (pas de logique nouvelle,
  // juste la même fonction utilitaire pure appelée depuis ce composant).
  const showFormPreview = useIsFormView();
  const variableGroups = useStore(({ variableGroups }) => variableGroups);
  const variables = useStore(({ variables }) => variables);
  const boxesFull = useStore(({ boxes }) => boxes);
  const allMasks = useStore(({ masks }) => masks);
  const containers = useStore(({ containers }) => containers);
  const columns = useStore(({ columns }) => columns);
  const rowContainers = useStore(({ rowContainers }) => rowContainers);
  const variablesGroupedByContainer = useMemo(() => {
    if (!showFormPreview) return [];
    return getVariablesGroupedByContainer({
      variableGroups,
      variables,
      boxes: [...boxesFull, ...allMasks.map((m) => m.boxes).flat()],
      containers,
      columns,
      rowContainers,
    });
  }, [showFormPreview, variableGroups, variables, boxesFull, allMasks, containers, columns, rowContainers]);

  const pdfRef = useRef(null);
  const panDragRef = useRef(null);
  // Même condition que renderContent : le document infini a son propre
  // scroller, dans les deux axes (cf. InfinitePage) ; #pdf ne défile plus.
  const showsInfinitePage = !showFormPreview && maskEditing.length === 0 &&
    (!proposalList || currentDocument.type === 'template');
  // Élément qui porte le scroll horizontal : celui du document infini, sinon
  // #pdf (édition de masque).
  const getHorizontalScroller = () => getCanvasScroller() ?? pdfRef.current;
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (fontFamilyList.length > 0) { applyFontsOnThePage(fontFamilyList) }
  }, [fontFamilyList]);

  // Rejoue la mesure au changement de zoom (le contenu zoomé change de
  // scrollWidth sans que le scroller lui-même redimensionne, donc le
  // ResizeObserver seul ne suffit pas), au changement de vue et à chaque resize
  // du scroller (resize fenêtre).
  useEffect(() => {
    const el = getHorizontalScroller();
    if (!el) return undefined;
    const updateCanScrollX = () => setCanScrollX(el.scrollWidth > el.clientWidth);
    updateCanScrollX();
    const observer = new ResizeObserver(updateCanScrollX);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCanScrollX, zoomLevel, showsInfinitePage]);

  // Le scroll redevient impossible (zoom redescendu) : on sort du mode main,
  // sinon le bouton disparaît de la navbar mais le curseur resterait en grab.
  useEffect(() => {
    if (!canScrollX && panModeActive) setPanModeActive(false);
  }, [canScrollX, panModeActive, setPanModeActive]);

  // Drag-to-pan (mode main) : mousemove/mouseup sur window pour continuer à
  // suivre la souris même si elle sort de #pdf pendant le drag.
  useEffect(() => {
    if (!panModeActive) return undefined;
    const handleMouseMove = (event) => {
      const scroller = getHorizontalScroller();
      if (!panDragRef.current || !scroller) return;
      const { startX, startY, scrollLeft, scrollTop } = panDragRef.current;
      scroller.scrollLeft = scrollLeft - (event.clientX - startX);
      scrollVListTo(scrollTop - (event.clientY - startY));
    };
    const handleMouseUp = () => {
      panDragRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      panDragRef.current = null;
      setIsDragging(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panModeActive]);

  const handlePanMouseDown = useCallback((event) => {
    const scroller = getHorizontalScroller();
    if (!panModeActive || event.button !== 0 || !scroller) return;
    event.preventDefault();
    panDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: scroller.scrollLeft,
      scrollTop: getVListScrollOffset(),
    };
    setIsDragging(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panModeActive]);

  const renderMaskEditing = () => {
    const maskType = masks.find((m) => m.id === MASKS_TYPE.HEADER.id)?.editableMask;
    const maskTypeEdiatble = typeof maskType !== 'boolean' ? true : maskType;
    // Le décor de page s'édite à sa place dans la page, entre l'en-tête et le
    // pied montrés comme repères.
    const isPageBackgroundEditing = maskEditing.length === 1 && isPageBackgroundId(maskEditing[0])
    const renderMask = isPageBackgroundEditing
      ? <PageBackgroundFrame editable />
      : masks.map((mask) => (
        <div key={mask.id} style={{ backgroundColor: 'white' }}>
          <Container id={mask.id} />
        </div>
      ));
    const isHeaderFooterMask = maskEditing.includes(MASKS_TYPE.FOOTER.id) || maskEditing.includes(MASKS_TYPE.HEADER.id)
    const defautlMaskHeight = (landscape ? pageWidth : pageHeight) + 8
    // +contentPadding : compense le paddingTop retiré du wrapper #pdf ci-dessous
    // (plus de gap visuel top bar/document), contentHeight le soustrayant encore.
    const maskContentHeight = isHeaderFooterMask ? contentHeight + contentPadding : defautlMaskHeight;
    return (
      <div
        style={{
          display: "flex",
          justifyContent: 'center',
          marginLeft: 0,
          width: "100%",
          // Place du rappel affiché au-dessus de la page pendant l'édition du décor.
          paddingTop: isPageBackgroundEditing ? PAGE_BACKGROUND_NOTICE_SPACE : 0,
          zoom: `${effectiveZoom(zoomLevel, landscape)}%`
        }}
        data-role={pageBackground}>
        <div
          style={{
            width: (landscape ? pageHeight : pageWidth),
            height: maskContentHeight,
            paddingBottom: 8,
            color: configuration.baseFontColor?.style,
            position: 'relative',
            justifyContent: 'space-between',
            display: 'flex',
            flexDirection: 'column'
          }
          }
          onClick={(event) => {
            // DEBUG clic sur la page
            // eslint-disable-next-line no-console
            console.log('[Middle] page click', {
              ctrlKey: event.ctrlKey,
              metaKey: event.metaKey,
              role: event.target.dataset.role,
              parent: event.target.dataset.parent,
            });
            // Ne pas désélectionner la box lorsqu'on clique dans un Select Ant Design
            // (champ ou dropdown : ex. `shapeTypeSelector` dans la navbar)
            if (event.target && typeof event.target.closest === 'function') {
              if (
                event.target.closest('.ant-select') ||
                event.target.closest('.ant-select-dropdown')
              ) {
                return;
              }
            }
            if (!(event.ctrlKey || event.metaKey || event.shiftKey) && event.target.dataset.role === 'pageBackground') {
              handleUnSelectBox();
              if (event.target.dataset.parent !== containerBackground) {
                handleUnSelectContainer()
              }
            }
          }}
          data-role='pageBackground'
          className='page'
        >
          {isHeaderFooterMask ?
            <>
              <div style={{
                position: 'absolute',
                borderLeft: "#00000021 dotted 3px",
                borderRight: "#00000021 dotted 3px",
                width: '100%',
                top: (header.height ?? masksDefaultHeight.header) + 3,
                height: contentHeight + contentPadding - 8 -
                  (header.height ?? masksDefaultHeight.header)
                  - (footer.height ?? masksDefaultHeight.footer)
                  - 6
              }} />
              <div style={{
                position: "absolute",
                top: (header.height ?? masksDefaultHeight.header),
                left: "50%",
                transform: "translateX(-50%)"
              }}>{MASKS_TYPE.HEADER.name.toUpperCase()}
                {!maskTypeEdiatble && (<div style={{
                  marginTop: 200,
                  display: "flex",
                  flexDirection: "column"
                }}>
                  <FontAwesomeIcon
                    icon={icon({ name: 'lock-keyhole', style: 'light' })}
                    style={{ margin: 0, fontSize: 70 }} />
                  <div>Masque bloqué</div>
                </div>)}
              </div>
              <div style={{
                position: "absolute",
                bottom: (footer.height ?? masksDefaultHeight.footer) + 8,
                left: "50%",
                transform: "translateX(-50%)"
              }}>{MASKS_TYPE.FOOTER.name.toUpperCase()}</div>
              {renderMask}
            </> : renderMask}
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (true) {
      case showFormPreview:
        // FormViewer attend une hauteur définie (VList virtualisé : flex:1 sur
        // un ancêtre de hauteur indéterminée = 0px mesuré = 0 ligne rendue,
        // même avec des données). #pdf lui-même n'a pas de hauteur propre (elle
        // dépend de son contenu), donc position:absolute ne marche pas non plus
        // (plus aucun contenu en flux normal pour lui donner une hauteur).
        // Hauteur calculée explicitement, comme OverlaySider/VariableHandler.
        return (
          <div style={{ height: `calc(100vh - ${navbarHeight}px)`, width: '100%' }}>
            <FormViewer variablesGroupedByContainer={variablesGroupedByContainer} />
          </div>
        )
      case maskEditing.length > 0:
        return renderMaskEditing();
      case (!proposalList || currentDocument.type === 'template'):
        return <InfinitePage />
      case proposalList:
        return <ProposalList />
      default:
        return null;
    }
  }


  return (
    <div
      id='pdf'
      ref={pdfRef}
      data-role={pageBackground}
      onMouseDown={handlePanMouseDown}
      style={{
        fontSize: `${fontSize}px`,
        overflowY: 'hidden',
        fontFamily: fontFamily,
        width: '100%',
        display: 'flex',
        cursor: isDragging ? 'grabbing' : panModeActive ? 'grab' : undefined,
        userSelect: panModeActive ? 'none' : undefined,
        // `safe center` : centré tant que le document (largeur fixe, cf. couche
        // zoomée dans InfinitePage) tient dans la zone, mais bascule en
        // alignement `start` dès qu'il déborde (fort zoom). `center` pur
        // pousserait le débord à gauche hors de portée du scroll (scrollLeft ne
        // peut pas être négatif) → boxes latérales inatteignables.
        justifyContent: 'safe center',
        // Un peu plus marqué que le gris quasi-blanc du body (#f0f2f5) pour
        // mieux distinguer la zone de travail de la page du document.
        backgroundColor: '#e4e6e9',
        // Plus de gap visuel entre la top bar et le document (cf. contentHeight
        // + contentPadding plus haut, qui compense pour garder le même budget
        // de hauteur en mode édition de masque).
        paddingTop: 0,
        // Décale le document vers la droite pour des marges égales de chaque
        // côté : le panel gauche (rail + contenu) est plus large que celui de
        // droite. paddingLeft déplace le centre du flex de la moitié de sa
        // valeur, d'où la différence de largeur complète des deux panels.
        // Pas en vue liste (proposalList) : les panels y sont masqués. Ni pour
        // le document infini, qui centre lui-même la page dans son scroller
        // pleine largeur (cf. InfinitePage) — #pdf ne défile alors plus.
        paddingLeft: proposalList || showsInfinitePage ? 0 : canvasLeftShift,
        overflow: showsInfinitePage ? 'hidden' : 'scroll',
        marginTop: navbarHeight,
        // Sans ça, le fond gris ne couvre que la hauteur du contenu : invisible
        // en édition (le document dépasse toujours l'écran) mais flagrant en
        // vue Liste des documents, dont le contenu est court et laisse
        // apparaître le blanc de la page en dessous.
        minHeight: `calc(100vh - ${navbarHeight}px)`,
        position: 'relative'
      }}
    >
      {/* Mode main : les box/containers ne doivent pas intercepter le
          mousedown (sinon le drag déclenche un clic/sélection dessous). */}
      <div style={{ pointerEvents: panModeActive ? 'none' : undefined, width: showsInfinitePage ? '100%' : undefined }}>
        {renderContent()}
      </div>
    </div>
  );
};

export default React.memo(Middle);
